import type {
  MapPosition2D,
  RoomMeasures,
  VFloor,
  VRoom,
  VScene,
  VTour,
} from "@/types";
import type { PendingScene } from "@ctx/TourContexts";
import * as FloorApi from "@a/floors";
import * as RoomApi from "@a/rooms";
import * as SceneApi from "@a/scenes";
import * as TourApi from "@a/tours";
import { useAuth } from "@h/useCtx";
import { getTourGrant } from "@/lib/tourGrant";
import { buildRoute } from "@u/routes";
import { useQueryClient } from "@tanstack/react-query";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  TourDataContext,
  TourSceneContext,
  type TourDataValue,
  type TourSceneValue,
} from "@ctx/TourContexts";

export const TourProvider = ({
  children,
  tourId,
  initialData,
  initialSceneId,
}: {
  children: React.ReactNode;
  tourId: string;
  initialData?: VTour;
  initialSceneId?: string;
}) => {
  const [tour, setTour] = useState<VTour | null>(initialData ?? null);
  const [allScenes, setScenes] = useState<VScene[]>([]);
  const [currentScene, setCurrentScene] = useState<VScene | null>(null);
  const [loadingScene, setLoadingScene] = useState(true);
  const [allFloors, setFloors] = useState<VFloor[]>([]);
  const [pendingScenes, setPendingScenes] = useState<PendingScene[]>([]);
  const [allRooms, setRooms] = useState<VRoom[]>([]);
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const isOwner = !!user && !!tour && user.id === tour.user_id;
  const canEdit = isOwner || isAdmin;
  const exitPath = isOwner || isAdmin ? buildRoute.tours() : "/";

  // Ref para acceder al tour actual sin incluirlo en deps de callbacks
  const tourRef = useRef<VTour | null>(null);
  useEffect(() => {
    tourRef.current = tour;
  }, [tour]);

  // Ref for refresh function to avoid circular references
  const refreshRef = useRef<(sceneId?: string) => Promise<void>>(() => Promise.resolve());

  // Capture initialSceneId once at mount — it's an initial value, not reactive
  const initialSceneIdRef = useRef(initialSceneId);

  // ── Activar escena — síncrono, sin async innecesario ──────────────────
  const activateScene = useCallback((scene: VScene) => {
    setCurrentScene((prev) =>
      prev?.scene_id === scene.scene_id ? prev : scene,
    );
  }, []);

  const clearTourState = useCallback(() => {
    setTour(null);
    setScenes([]);
    setCurrentScene(null);
  }, []);

  const clearScene = useCallback(() => setCurrentScene(null), []);

  // ── Refresh — recarga escenas y activa la correcta ────────────────────
  const refresh = useCallback(
    async (preferredSceneId?: string) => {
      setLoadingScene(true);
      try {
        // Password-protected tours are hidden from anon at the RLS layer. A
        // visitor who unlocked one holds a grant token → read the scene graph
        // through the server gate instead of the (now-blocked) table reads. The
        // owner/admin never has a token and keeps the normal path (their RLS
        // branch still allows table reads), so editor + owner-preview are
        // unchanged, as are all non-password tours.
        const grantToken = getTourGrant(tourId);
        let scenesData: VScene[];
        let floorsData: VFloor[];
        let roomsData: VRoom[];
        if (grantToken) {
          const graph = await TourApi.fetchTourViewGraph(grantToken);
          if (!graph) throw new Error("Tour access expired");
          scenesData = graph.scenes;
          floorsData = graph.floors;
          roomsData = graph.rooms;
          // Seed the per-scene hotspot cache so useHotspotActions' query resolves
          // from cache instead of hitting the (blocked) v_hotspots table.
          const byScene = new Map<string, typeof graph.hotspots>();
          for (const h of graph.hotspots) {
            const arr = byScene.get(h.scene_id) ?? [];
            arr.push(h);
            byScene.set(h.scene_id, arr);
          }
          for (const s of scenesData) {
            queryClient.setQueryData(
              ["hotspots", s.scene_id],
              byScene.get(s.scene_id) ?? [],
            );
          }
        } else {
          [scenesData, floorsData, roomsData] = await Promise.all([
            SceneApi.fetchTourScenesView(tourId),
            FloorApi.fetchFloorsByTour(tourId),
            RoomApi.fetchRoomsByTour(tourId),
          ]);
        }

        // Solo fetch del tour si no lo tenemos aún
        if (!tourRef.current) {
          const tourData = await TourApi.fetchTourById(tourId);
          if (!tourData) throw new Error("Tour not found");
          setTour(tourData);
        }

        setFloors(floorsData as VFloor[]);
        setRooms(roomsData);
        setScenes(scenesData);

        // Solo intentamos activar escena si hay alguna
        if (scenesData.length > 0) {
          const next =
            (preferredSceneId
              ? scenesData.find((s) => s.scene_id === preferredSceneId)
              : null) ?? SceneApi.getInitialScene(scenesData);
          if (next) activateScene(next);
        }
      } catch (error: unknown) {
        const err = error as { status?: number; message?: string; code?: string };
        // 1. Logging estructurado solo para desarrollo
        if (import.meta.env.DEV) {
          console.error("[TourContext.refresh] Critical Failure:", error);
        }

        // 2. Identificación del error para feedback preciso
        const isAuthError =
          err?.status === 401 || err?.message?.includes("JWT");
        const isNotFoundError =
          err?.status === 404 || err?.code === "PGRST116";

        // 3. Notificación al usuario vía Sonner
        toast.error(t("tour_load_failed"), {
          description: isAuthError
            ? t("session_expired_refresh")
            : isNotFoundError
              ? t("tour_not_found")
              : t("check_connection"),
          action: isAuthError
            ? {
                label: t("login"),
                onClick: () => (window.location.href = buildRoute.login()),
              }
            : {
                label: t("retry"),
                onClick: () => {
                  if (refreshRef.current) void refreshRef.current();
                },
              },
        });

        // 4. Limpieza de estado para evitar "zombie data" en el viewer
        clearTourState();
      } finally {
        setLoadingScene(false);
      }
    },
    [tourId, activateScene, clearTourState, queryClient, t],
  );

  // Store refresh in ref for use in error toast
  useEffect(() => {
    refreshRef.current = refresh;
  }, [refresh]);

  // Load scenes when tourId changes — use ref so the rule can't
  // statically trace through to the state setters inside refresh.
  // refreshRef.current is always the latest refresh (kept in sync above).
  useEffect(() => {
    if (tourId) {
      void refreshRef.current(initialSceneIdRef.current);
    }
  }, [tourId]); // initialSceneId es un valor inicial — solo aplica al primer cargado

  const updateSceneLocal = useCallback(
    (sceneId: string, updates: Partial<VScene>) => {
      setScenes((prev) =>
        prev.map((s) => (s.scene_id === sceneId ? { ...s, ...updates } : s)),
      );
      setCurrentScene((prev) =>
        prev?.scene_id === sceneId ? { ...prev, ...updates } : prev,
      );
    },
    [],
  );

  const updateRoomLocal = useCallback(
    (roomId: string, updates: Partial<VRoom>) => {
      setRooms((prev) =>
        prev.map((r) => (r.id === roomId ? { ...r, ...updates } : r)),
      );
    },
    [],
  );

  const updateTourLocal = useCallback(
    (updates: Partial<VTour>) => {
      setTour((prev) => (prev ? { ...prev, ...updates } : prev));
    },
    [],
  );

  const updateFloorLocal = useCallback(
    (floorId: string, updates: Partial<VFloor>) => {
      setFloors((prev) =>
        prev.map((f) => (f.id === floorId ? { ...f, ...updates } : f)),
      );
    },
    [],
  );

  const addFloorLocal = useCallback((floor: VFloor) => {
    setFloors((prev) => [...prev, floor]);
  }, []);

  const addSceneLocal = useCallback((scene: VScene) => {
    setScenes((prev) => [...prev, scene]);
  }, []);

  const addRoomLocal = useCallback((room: VRoom) => {
    setRooms((prev) => [...prev, room]);
  }, []);

  const removeSceneLocal = useCallback((sceneId: string) => {
    setScenes((prev) => prev.filter((s) => s.scene_id !== sceneId));
  }, []);

  const removeRoomLocal = useCallback((roomId: string) => {
    setRooms((prev) => prev.filter((r) => r.id !== roomId));
  }, []);

  const removeFloorLocal = useCallback((floorId: string) => {
    setFloors((prev) => prev.filter((f) => f.id !== floorId));
  }, []);

  const addPendingScene = useCallback((scene: PendingScene) => {
    setPendingScenes((prev) => [...prev, scene]);
  }, []);

  const removePendingScene = useCallback((id: string) => {
    setPendingScenes((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const updatePendingSceneProgress = useCallback((id: string, progress: number) => {
    setPendingScenes((prev) =>
      prev.map((s) => (s.id === id ? { ...s, progress } : s)),
    );
  }, []);

  // ── Seleccionar escena — solo busca en el estado local ────────────────
  const selectScene = useCallback(
    (sceneId: string) => {
      if (currentScene?.scene_id === sceneId) return;
      const next = allScenes.find((s) => s.scene_id === sceneId);
      if (next) activateScene(next);
    },
    [allScenes, currentScene?.scene_id, activateScene],
  );

  // ── Actualizar posición local del mapa ────────────────────────────────
  const updateRoomMapPosLocal = useCallback(
    (roomId: string, pos: MapPosition2D | null) => {
      setScenes((prev) =>
        prev.map((scene) =>
          scene.room_id === roomId ? { ...scene, room_map_pos: pos } : scene,
        ),
      );
    },
    [],
  );

  const updateRoomShapeLocal = useCallback(
    (roomId: string, shape: MapPosition2D[] | null, measures?: RoomMeasures) => {
      setScenes((prev) =>
        prev.map((scene) =>
          scene.room_id === roomId
            ? {
                ...scene,
                room_shape: shape,
                // Mirror the canonical measure fields onto every scene of the
                // room so markers/labels reading v_scenes stay fresh pre-refetch.
                ...(measures?.edge_lengths !== undefined && {
                  room_edge_lengths: measures.edge_lengths,
                }),
                ...(measures?.height_m !== undefined && {
                  room_height_m: measures.height_m,
                }),
                ...(measures?.mts2 !== undefined && {
                  room_mts2: measures.mts2,
                }),
              }
            : scene,
        ),
      );
    },
    [],
  );

  const updateRoomShapeHiddenLocal = useCallback(
    (roomId: string, hidden: boolean) => {
      setScenes((prev) =>
        prev.map((scene) =>
          scene.room_id === roomId
            ? { ...scene, room_shape_hidden: hidden }
            : scene,
        ),
      );
    },
    [],
  );

  const dataValue = useMemo<TourDataValue>(
    () => ({
      tour,
      allScenes,
      allRooms,
      allFloors,
      isOwner,
      canEdit,
      exitPath,
      refresh,
      updateTourLocal,
      updateRoomMapPosLocal,
      updateRoomShapeLocal,
      updateRoomShapeHiddenLocal,
      updateSceneLocal,
      updateRoomLocal,
      updateFloorLocal,
      addFloorLocal,
      addSceneLocal,
      addRoomLocal,
      removeSceneLocal,
      removeRoomLocal,
      removeFloorLocal,
      pendingScenes,
      addPendingScene,
      removePendingScene,
      updatePendingSceneProgress,
    }),
    [
      tour,
      allScenes,
      allRooms,
      allFloors,
      isOwner,
      canEdit,
      exitPath,
      refresh,
      updateTourLocal,
      updateRoomMapPosLocal,
      updateRoomShapeLocal,
      updateRoomShapeHiddenLocal,
      updateSceneLocal,
      updateRoomLocal,
      updateFloorLocal,
      addFloorLocal,
      addSceneLocal,
      addRoomLocal,
      removeSceneLocal,
      removeRoomLocal,
      removeFloorLocal,
      pendingScenes,
      addPendingScene,
      removePendingScene,
      updatePendingSceneProgress,
    ],
  );

  const sceneValue = useMemo<TourSceneValue>(
    () => ({
      currentScene,
      loadingScene,
      selectScene,
      clearScene,
    }),
    [currentScene, loadingScene, selectScene, clearScene],
  );

  return (
    <TourDataContext.Provider value={dataValue}>
      <TourSceneContext.Provider value={sceneValue}>
        {children}
      </TourSceneContext.Provider>
    </TourDataContext.Provider>
  );
};
